import React, { useEffect, useState } from "react";
import { ConfirmDialog } from "../../../../common/dashboard/ConfirmDialog";
import FieldLabel from "../../../../common/dashboard/FieldLabel";
import SectionSaveReset, { SectionSaveResetProps } from "../../../../common/dashboard/SectionSaveReset";
import { GovernanceSettings } from "../settingsData";
import { applyCmpOrganizationNoun } from "../../../../../utils/cmpOrganizationNoun";
import { useCmpOrganizationNoun } from "../../../../../contexts/CmpOrganizationNounContext";
import {
  settingsInputClassName,
  settingsPrimaryButtonClassName,
  settingsRowCancelButtonClassName,
  settingsRowDeleteButtonClassName,
  settingsRowEditButtonClassName,
  settingsRowSaveButtonClassName,
  settingsSectionCardClassName,
  settingsSectionDescriptionClassName,
  settingsSectionTitleClassName,
  settingsTableHeaderClassName,
} from "../common/settingsStyles";

type GroupListKey = keyof GovernanceSettings;
const groupLists: Array<{
  key: GroupListKey;
  title: string;
  singular: string;
  label: string;
  description: string;
  emptyText: string;
}> = [
  {
    key: "boards", title: "Board of Directors", singular: "board", label: "Board",
    description: "Enter the names of the boards your chamber governs. Names entered here appear under the Board of Directors subheading in Member Involvement on every member profile. Renaming a board updates it everywhere; deleting one removes it as an option.",
    emptyText: "No boards created yet.",
  },
  {
    key: "committees", title: "Committees", singular: "committee", label: "Committee",
    description: "Enter as many committees as your chamber needs, each with its own custom name. Committees entered here appear automatically under Member Involvement on every member profile. Renaming a committee updates it everywhere; deleting one removes it as an option.",
    emptyText: "No committees created yet.",
  },
  {
    key: "taskForces", title: "Task Forces", singular: "task force", label: "Task Force",
    description: "Enter as many task forces as your chamber needs, each with its own custom name. Task forces entered here appear automatically under Member Involvement on every member profile and can be renamed or deleted at any time.",
    emptyText: "No task forces created yet.",
  },
];
const emptyNames = (): Record<GroupListKey, string> => ({ boards: "", committees: "", taskForces: "" });
type GroupDraft = { key: GroupListKey; id: string; name: string };

type GovernanceGroupsTabProps = {
  settings: GovernanceSettings;
  onChange: (settings: GovernanceSettings) => void;
  sectionControls: SectionSaveResetProps;
  onDirtyChange: (dirty: boolean) => void;
};

const GovernanceGroupsTab = ({ settings, onChange, sectionControls, onDirtyChange }: GovernanceGroupsTabProps) => {
  const noun = useCmpOrganizationNoun();
  const [names, setNames] = useState(emptyNames);
  const [errors, setErrors] = useState(emptyNames);
  const [editing, setEditing] = useState<GroupDraft | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<GroupDraft | null>(null);
  const hasLocalDraft = Object.values(names).some(Boolean) || Boolean(editing &&
    editing.name !== settings[editing.key].find(({ id }) => id === editing.id)?.name);

  useEffect(() => { onDirtyChange(hasLocalDraft); }, [hasLocalDraft, onDirtyChange]);
  useEffect(() => () => onDirtyChange(false), [onDirtyChange]);

  const getNameError = (key: GroupListKey, name: string, currentId?: string): string | null => {
    const trimmed = name.trim();
    const list = groupLists.find((item) => item.key === key)!;
    if (!trimmed) return `Enter the ${list.label} name.`;
    if (trimmed.length > 120) return "Enter a name of 120 characters or fewer.";
    return settings[key].some((group) => group.id !== currentId && group.name.trim().toLowerCase() === trimmed.toLowerCase())
      ? `A ${list.singular} with this name already exists.` : null;
  };

  const addGroup = (key: GroupListKey) => {
    const error = getNameError(key, names[key]);
    if (error) { setErrors((current) => ({ ...current, [key]: error })); return; }
    onChange({ ...settings, [key]: [...settings[key], {
      id: `governance-${key}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      name: names[key].trim(),
    }] });
    setNames((current) => ({ ...current, [key]: "" }));
    setErrors((current) => ({ ...current, [key]: "" }));
  };

  const saveEdit = () => {
    if (!editing) return;
    const error = getNameError(editing.key, editing.name, editing.id);
    if (error) { setEditError(error); return; }
    onChange({ ...settings, [editing.key]: settings[editing.key].map((group) =>
      group.id === editing.id ? { ...group, name: editing.name.trim() } : group) });
    setEditing(null);
    setEditError(null);
  };

  const reset = () => {
    setNames(emptyNames());
    setErrors(emptyNames());
    setEditing(null);
    setEditError(null);
    setPendingDelete(null);
    sectionControls.onReset();
  };
  const deleteList = groupLists.find(({ key }) => key === pendingDelete?.key);

  return (
    <div className="space-y-6">
      {groupLists.map(({ key, title, singular, label, description, emptyText }) => (
        <section key={key} className={settingsSectionCardClassName} aria-labelledby={`governance-${key}-heading`}>
          <h2 id={`governance-${key}-heading`} className={`${settingsSectionTitleClassName} text-[18px]`}>{title}</h2>
          <p className={settingsSectionDescriptionClassName}>{applyCmpOrganizationNoun(description, noun)}</p>
          <div className="mt-7 max-w-[600px]">
            <FieldLabel label={`${label} Name`} helper={`Enter the name of the ${singular}, then select + Add ${label} to add it to the list below.`}>
              <input
                aria-label={`${label} name`}
                aria-invalid={Boolean(errors[key])}
                aria-describedby={errors[key] ? `governance-${key}-error` : undefined}
                className={`${settingsInputClassName} w-full border-[#9E9E9E] ${errors[key] ? "border-red-500" : ""}`}
                maxLength={120}
                placeholder={`Enter ${label} Name`}
                value={names[key]}
                onChange={(event) => {
                  setNames((current) => ({ ...current, [key]: event.target.value }));
                  setErrors((current) => ({ ...current, [key]: "" }));
                }}
                onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); addGroup(key); } }}
              />
            </FieldLabel>
            {errors[key] && <p id={`governance-${key}-error`} role="alert" className="mt-2 text-sm text-red-600">{errors[key]}</p>}
            <button type="button" className={`${settingsPrimaryButtonClassName} mt-2 px-6`} disabled={!names[key].trim()} onClick={() => addGroup(key)}>+ Add {label}</button>
          </div>
          <div className="mt-6" role="table" aria-label={`${title} groups`}>
            <div className="grid grid-cols-1 overflow-hidden rounded-[8px] bg-[#F7F7F8] lg:grid-cols-[minmax(0,1fr)_190px]" role="row">
              <div className={settingsTableHeaderClassName} role="columnheader">{label}</div>
              <div className={`${settingsTableHeaderClassName} hidden lg:block`} role="columnheader">Actions</div>
            </div>
            {!settings[key].length && <p className="px-4 py-5 text-sm text-[#727272]">{emptyText}</p>}
            {settings[key].map((group) => {
              const isEditing = editing?.key === key && editing.id === group.id;
              return <div key={group.id} role="row" className="grid grid-cols-1 items-center border-b border-[#ECECEC] lg:grid-cols-[minmax(0,1fr)_190px]">
                <div role="cell" className="min-w-0 px-4 py-3 text-sm text-[#0A0A0A]">
                  {isEditing ? <>
                    <input aria-label={`Edit ${singular} name`} className={`${settingsInputClassName} w-full max-w-[420px] border-cpaGold`} maxLength={120}
                      value={editing.name} aria-invalid={Boolean(editError)}
                      onChange={(event) => { setEditing({ ...editing, name: event.target.value }); setEditError(null); }}
                      onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); saveEdit(); } }} />
                    {editError && <p role="alert" className="mt-2 text-sm text-red-600">{editError}</p>}
                  </> : <span className="break-words">{group.name}</span>}
                </div>
                <div role="cell" className="flex flex-wrap items-center gap-2 px-4 pb-3 lg:py-3">
                  {isEditing ? <>
                    <button type="button" className={settingsRowSaveButtonClassName} disabled={!editing.name.trim()} onClick={saveEdit}>Save</button>
                    <button type="button" className={settingsRowCancelButtonClassName} onClick={() => { setEditing(null); setEditError(null); }}>Cancel</button>
                  </> : <>
                    <button type="button" className={settingsRowEditButtonClassName} aria-label={`Edit ${group.name}`} onClick={() => { setEditing({ key, ...group }); setEditError(null); }}>Edit</button>
                    <button type="button" className={settingsRowDeleteButtonClassName} aria-label={`Delete ${group.name}`} onClick={() => setPendingDelete({ key, ...group })}>Delete</button>
                  </>}
                </div>
              </div>;
            })}
          </div>
        </section>
      ))}
      <SectionSaveReset {...sectionControls} isDirty={sectionControls.isDirty || hasLocalDraft} saveDisabled={hasLocalDraft} onReset={reset} />
      <ConfirmDialog open={Boolean(pendingDelete)} variant="destructive"
        title={`Delete ${deleteList?.singular || "group"}?`}
        message={`This removes the ${deleteList?.singular || "group"} as an option on every member profile and in the email audience choices.`}
        confirmLabel="Delete" cancelLabel="Cancel" onClose={() => setPendingDelete(null)}
        onConfirm={() => {
          if (!pendingDelete) return;
          onChange({ ...settings, [pendingDelete.key]: settings[pendingDelete.key].filter(({ id }) => id !== pendingDelete.id) });
          if (editing?.id === pendingDelete.id) { setEditing(null); setEditError(null); }
          setPendingDelete(null);
        }} />
    </div>
  );
};

export default GovernanceGroupsTab;
